import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ApiService } from '@core/api.service';
import { AuthService } from '@features/auth/auth.service';
import { EntityEditService } from '@services/common/entity-edit.service';
import { includeSubprojects } from '@shared/utility/map-subprojects';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { handleError } from '@ui-kit/utility/handleHttpErrorResponse';
import { CrudService, GetEntityModel } from 'collection';
import { Collection, CollectionModel, Item } from 'collection';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'create-checklist-modal',
  templateUrl: './create-checklist-modal.component.html',
  styleUrls: ['./create-checklist-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateChecklistModalComponent implements OnInit, OnDestroy {
  private _crud = inject(CrudService);

  projectsCollection: CollectionModel;
  categoryCollection: CollectionModel;
  templatesCollection: CollectionModel;

  projectTemplatesRequest: GetEntityModel = this._crud.createGetEntity({ name: 'checklist/templates' });
  projectHasTemplates = false;

  checklistItem: Item;
  categoryItem: Item;

  formGroup: UntypedFormGroup;

  projectModel: any = null;
  categoryModel: any = null;
  templateModel: any = null;

  isEmployee: boolean;
  isAdmin: boolean;

  private _destroyed$ = new Subject();

  close(): void {
    this._dialogRef.close();
  }

  initForm() {
    this.formGroup = new UntypedFormGroup({
      project_id: new UntypedFormControl(null, Validators.required),
      category_id: new UntypedFormControl(null),
      template_id: new UntypedFormControl(null, Validators.required)
    });

    if (this.data.item.hasOwnProperty('id') && this.data.item.id) {
      this.getFormControl('project_id').setValue(this.data.item.id);
      this.projectModel = { name: this.data.item.name, id: this.data.item.id };
      this.checkTemplatesProject();
    }
  }

  getProjectsList({ search, page }): void {
    this.projectsCollection.setParams('page', page);

    search.length
      ? this.projectsCollection.setParams('search_query', search)
      : this.projectsCollection.clearParams('search_query');
    this.projectsCollection.reload().subscribe();
  }

  getCategoryList({ search, page }): void {
    this.categoryCollection.setParams('page', page);
    search.length ? this.categoryCollection.setParams('name', search) : this.categoryCollection.clearParams('name');
    this.categoryCollection.reload().subscribe();
  }

  getTemplateList({ search, page }): void {
    const categoryId = this.getFormControl('category_id').value;
    const projectId = this.getFormControl('project_id').value;

    if (categoryId) {
      this.templatesCollection.setParams('category_id', categoryId);
    } else {
      this.templatesCollection.clearParams('category_id');
    }

    if (projectId && this.projectHasTemplates) {
      this.templatesCollection.setParams('project_id', projectId);
      this.templatesCollection.clearParams('companyTemplates');
    } else {
      this.templatesCollection.clearParams('project_id');
      this.templatesCollection.setParams('companyTemplates', true);
    }

    this.templatesCollection.setParams('page', page);
    search.length ? this.templatesCollection.setParams('name', search) : this.templatesCollection.clearParams('name');
    this.templatesCollection.reload().subscribe();
  }

  onSelectProject({ model }): void {
    this.projectModel = model;
    this.getFormControl('project_id').setValue(model.id);
    this.clearTemplate();
    this.checkTemplatesProject();
  }

  checkTemplatesProject(): void {
    const projectId = this.getFormControl('project_id').value;

    this.projectTemplatesRequest
      .get({ query: { project_id: projectId, page: 1, per_page: 1 } })
      .pipe(
        map(({ data }) => {
          this.projectHasTemplates = !!data.items.length;
        })
      )
      .subscribe();
  }

  cleanTemplateField(): void {
    const templateId = this.getFormControl('template_id').value;
    const categoryId = this.getFormControl('category_id').value;

    if (templateId && categoryId) {
      this.getFormControl('template_id').setValue(null);
      this.templateModel = null;
    }
  }

  onSelectedCategory({ model }): void {
    if (!model.id) {
      this.cleanTemplateField();
    }

    this.getFormControl('category_id').setValue(model.id);
    this.categoryModel = model;
    this.clearTemplate();
  }

  onSelectedTemplate({ model }): void {
    this.getFormControl('template_id').setValue(model.id);

    if (model.category_id) {
      this.autoCompleteCategory(model.category_id);
    }

    this.templateModel = model;
  }

  clearTemplate(): void {
    this.getFormControl('template_id').setValue(null);
    this.templateModel = null;
  }

  autoCompleteCategory(id: string | number): void {
    const categoryControl = this.getFormControl('category_id');
    categoryControl.setValue(id);

    this.loadCategoryItem(id)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((categoryItem) => {
        this.categoryModel = categoryItem;
        this._cds.markForCheck();
      });
  }

  openCreateProjectModal() {
    this._entityService
      .openProjectModal()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(({ confirmed, item }) => {
        if (confirmed) {
          const control = this.getFormControl('project_id');
          control.setValue(item.id);
          this.projectModel = {
            ...item,
            name: `${item.name} (${item.display_id})`
          };
          this._cds.markForCheck();
        }
      });
  }

  private createChecklist(): void {
    const data = {
      project_id: this.getFormControl('project_id').value,
      template_id: this.getFormControl('template_id').value
    };

    this.checklistItem.createItem({ data }).subscribe(
      (res: { data: any }) => {
        this.data.subject.next({
          confirmed: true,
          modal: this._dialogRef,
          item: res.data
        });

        this._dialogRef.close();

        this._toastService.show({
          type: 'success',
          message: marker('Checklist is successfully created')
        });
      },
      (err) => {
        const errorResponse = err.error;
        handleError(errorResponse, this._toastService);
      }
    );
  }

  submit(): void {
    if (this.formGroup.valid) {
      this.createChecklist();
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  private getFormControl(controlName: string): AbstractControl {
    return this.formGroup.get(controlName);
  }

  private collectionOnLoadSubscription(): void {
    this.projectsCollection.onLoad$.pipe(takeUntil(this._destroyed$)).subscribe((res: { items: any }) => {
      includeSubprojects(res.items.length, this.projectsCollection.items);
    });
  }

  private loadCategoryItem(categoryId: number | string): Observable<any> {
    this.categoryItem.setParams('id', categoryId);
    return this.categoryItem.load();
  }

  ngOnInit(): void {
    this.initForm();
    this.collectionOnLoadSubscription();
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
  }

  constructor(
    private _auth: AuthService,
    public _dialogRef: MatDialogRef<any>,
    private _entityService: EntityEditService,
    private _cds: ChangeDetectorRef,
    private _api: ApiService,
    private _toastService: ToasterService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isEmployee = this._auth.isWorker();
    this.isAdmin = this._auth.isAdmin();

    this.projectsCollection = new Collection({
      api: this._crud.createEntity({ name: 'project/list-trigger' }),
      params: {
        expand: 'client,subprojects,subprojects.client',
        sort: 'name',
        'exclude_display_status[]': 'finished'
      }
    });
    this.projectsCollection.loadByScrolling = true;

    this.categoryCollection = new Collection({
      api: this._crud.createEntity({ name: 'checklist/categories' }),
      params: {
        sort: 'name',
        with_templates: 'true'
      }
    });
    this.categoryCollection.loadByScrolling = true;

    this.templatesCollection = new Collection({
      api: this._crud.createEntity({ name: 'checklist/templates' }),
      params: {
        sort: 'name'
      }
    });
    this.templatesCollection.loadByScrolling = true;

    this.checklistItem = new Item({
      api: this._api.checklists
    });

    this.categoryItem = new Item({
      api: this._crud.createGetEntity({ name: 'checklist/categories' })
    });
  }
}
